<!--
 * @Author: guoyu
 * @Date: 2020-06-09 15:20:18
 * @LastEditors: your name
 * @LastEditTime: 2020-06-15 17:51:56
 * @Description: 环形进度条
-->
<template>
  <div>
    <svg :viewBox="viewBox">
      <defs>
        <filter
          :id="filterId"
          x="0"
          y="0"
          filterUnits="userSpaceOnUse"
          color-interpolation-filters="sRGB"
        >
          <feFlood flood-opacity="0" result="BackgroundImageFix" />
          <feBlend
            mode="normal"
            in="SourceGraphic"
            in2="BackgroundImageFix"
            result="shape"
          />
          <feColorMatrix
            in="SourceAlpha"
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0"
            result="hardAlpha"
          />
          <feOffset dy="20" />
          <feGaussianBlur stdDeviation="20" />
          <feComposite in2="hardAlpha" operator="arithmetic" k2="-1" k3="1" />
          <feColorMatrix
            type="matrix"
            values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"
          />
          <feBlend mode="normal" in2="shape" result="effect1_innerShadow" />
        </filter>
        <linearGradient :id="uid" x1="100%" y1="0%" x2="0%" y2="0%">
          <stop
            v-for="{ key, index } in LinearGradient"
            :key="index"
            :offset="key"
            :stop-color="color[key]"
          />
        </linearGradient>
      </defs>
      <path
        :filter="shadow ? 'url(#' + filterId + ')' : ''"
        :style="hoverStyle"
        :d="path"
      />
      <path
        :d="path"
        class="layer"
        :style="layerStyle"
        :stroke="gradient ? `url(#g${uid})` : color"
      />
      <slot name="inner"></slot>
    </svg>
    <slot :viewBox="viewBox"></slot>
  </div>
</template>
<script>
import { raf, cancelRaf } from '../utils/raf';
let uid = 0;
export default {
  model: {
    event: 'change',
    prop: 'value',
  },
  props: {
    strokeLinecap: {
      type: String,
      default: 'round',
    },
    value: {
      type: Number,
      default: 0,
    },
    speed: {
      type: [Number, String],
      default: 0,
    },
    fill: {
      type: String,
      default: 'none',
    },
    rate: {
      type: [Number, String],
      default: 100,
    },
    layerColor: {
      type: String,
      default: 'rgba(155, 155, 155, 0.2)',
    },
    color: {
      type: [String, Object],
      default: 'rgb(25, 137, 250)',
    },
    strokeWidth: {
      type: [Number, String],
      default: 40,
    },
    // 顺时针｜逆时针
    clockwise: {
      type: Boolean,
      default: true,
    },
    // 进度条总长
    PERIMETER: {
      type: [String, Number],
      default: 3140,
    },
    // 自定义进度条
    pathFn: {
      type: Function,
      default: getPath,
    },
    // 进度条超出容器宽度
    overWidth: {
      type: [Number, String],
      default: 0,
    },
    // 是否展示阴影
    shadow: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    filterId() {
      return 'f' + this.uid
    },
    gradientId() {
      return 'g' + this.uid
    },
    path() {
      return this.pathFn(this.clockwise, this.viewBoxSize);
    },

    viewBoxSize() {
      return +this.strokeWidth + 1000;
    },
    viewBox() {
      return `0 0 ${this.viewBoxSize} ${this.viewBoxSize}`;
    },
    layerStyle() {
      let startPercent = this.speed ? this.startRate : this.value;
      if (this.increase) {
        startPercent =
          this.startRate > this.value ? this.value : this.startRate;
      } else {
        startPercent =
          this.startRate < this.value ? this.value : this.startRate;
      }

      const offset = (this.PERIMETER * startPercent) / 100;
      return {
        fill: `${this.fill}`,
        stroke: `${this.color}`,
        strokeWidth: `${+this.strokeWidth + this.overWidth}px`,
        strokeLinecap: this.strokeLinecap,
        strokeDasharray: `${offset}px ${this.PERIMETER}px`,
      };
    },

    hoverStyle() {
      return {
        fill: `${this.fill}`,
        stroke: `${this.layerColor}`,
        strokeWidth: `${this.strokeWidth}px`,
        strokeLinecap: this.strokeLinecap,
      };
    },

    gradient() {
      return Object.prototype.toString.call(this.color) === '[object Object]';
    },

    LinearGradient() {
      if (!this.gradient) {
        return;
      }
      return Object.keys(this.color).sort(
        (a, b) => parseFloat(a) - parseFloat(b),
      );
    },
  },
  data() {
    return {
      uid: 0,
      startTime: '',
      startRate: '',
      endRate: '',
      increase: '',
      duration: '',
      rafId: '',
    };
  },
  watch: {
    value(newVal, oldVal) {
      this.startTime = Date.now();
      this.startRate = oldVal;
      this.endRate = format(newVal);
      this.increase = this.endRate > this.startRate;
      this.duration = Math.abs(
        ((this.startRate - this.endRate) * 1000) / this.speed,
      );
      if (this.speed) {
        cancelRaf(this.rafId);
        this.rafId = raf(this.animate);
      }
    },
  },

  methods: {
    animate() {
      const now = Date.now();
      const progress = Math.min((now - this.startTime) / this.duration, 1);
      this.startRate =
        progress * (this.endRate - this.startRate) + this.startRate;
      let rate = this.startRate;
      if (this.increase ? rate < this.endRate : rate > this.endRate) {
        this.rafId = raf(this.animate);
      }
    },
  },
  created() {
    uid++
    this.uid = uid;
  },
};
/**
 * @description: 弧形路径
 */
function getPath(clockwise, viewBoxSize) {
  const sweepFlag = clockwise ? 1 : 0;
  return `M ${viewBoxSize / 2} ${viewBoxSize /
    2} m 0, -500 a 500, 500 0 1, ${sweepFlag} 0, 1000 a 500, 500 0 1, ${sweepFlag} 0, -1000`;
}
function format(rate) {
  return Math.min(Math.max(rate, 0), 100);
}
</script>
<style lang="scss" scoped></style>
